import { SharedProjectsView } from './SharedProjectsView'
import { AdminDashboard } from '../Admin/AdminDashboard'
import { SupportPanel } from '../Support/SupportPanel'
import { ChatHistoryPanel } from './ChatHistoryPanel'
import { IntegrationsPanel } from '../Integrations/IntegrationsPanel'
import { useState, useRef, useEffect, useCallback } from 'react'
import { useAuthStore } from '../../auth/useAuthStore'
import { apiUrl, authFetch } from '../../lib/api'
import { isCloudMode } from '../../lib/storageMode'
import { DatabasePanel } from '../DatabasePanel/DatabasePanel'
import { SecretsPanel } from '../SecretsPanel/SecretsPanel'
import { DeployPanel } from '../DeployPanel/DeployPanel'
import { AnalyticsDashboard } from '../Analytics/AnalyticsDashboard'
import { SecurityScannerPanel } from '../SecurityScanner/SecurityScannerPanel'
import { TemplatesPanel } from '../TemplatesPanel/TemplatesPanel'
import { CronPanel } from '../CronJobs/CronPanel'
import { CommunityPage } from '../Community/CommunityPage'
import { SettingsPanel } from '../Settings/SettingsPanel'
import { BillingDashboard } from '../BillingDashboard/BillingDashboard'
import { ComponentLibraryPanel } from '../ComponentLibrary/ComponentLibraryPanel'
import { MarketplacePanel } from '../Marketplace/MarketplacePanel'
import { PackagePanel } from '../PackagePanel/PackagePanel'
import { CICDPanel } from '../CICDPanel/CICDPanel'
import { DebuggerPanel } from '../Debugger/DebuggerPanel'
import { TeamWorkspaces } from '../TeamWorkspaces/TeamWorkspaces'
import { CodeComments } from '../Comments/CodeComments'
import { VersionHistory } from '../VersionHistory/VersionHistory'
import { CodeReviewPanel } from '../CodeReview/CodeReviewPanel'
import { DocGenPanel } from '../DocGen/DocGenPanel'
import { ThemeMarketplace } from '../ThemeMarketplace/ThemeMarketplace'

interface Project {
  id: string; name: string; framework?: string; updated_at?: string
  preview_url?: string; deploy_url?: string; description?: string; starred?: boolean
  slug?: string
}

function ProjectPreviewView({ project, api, token, onServerStarted }: { project: Project; api: string; token: string | null; onServerStarted?: (slug: string, port: number) => void }) {
  const [devPort, setDevPort] = useState<number | null>(null)
  const [starting, setStarting] = useState(false)
  const [error, setError] = useState('')
  const [refreshKey, setRefreshKey] = useState(0)
  const isStatic = project.framework === 'html'

  // Reload preview when agent patches files
  useEffect(() => {
    const handler = () => setRefreshKey(k => k + 1)
    window.addEventListener('pulse:preview-refresh', handler)
    return () => window.removeEventListener('pulse:preview-refresh', handler)
  }, [])

  // For deployed projects, show deploy URL
  if (project.deploy_url) {
    return <div style={{ flex: 1 }}><iframe key={refreshKey} src={project.deploy_url} sandbox="allow-scripts allow-same-origin" style={{ width: '100%', height: '100%', border: 'none', borderRadius: 8, background: '#fff' }} /></div>
  }

  // If dev server is running, show it for any project type
  if (devPort) {
    return (
      <div style={{ flex: 1, position: 'relative' }}>
        <iframe
          src={`http://localhost:${devPort}`}
          onLoad={() => {
            // Save a snapshot of the running app for thumbnail
            setTimeout(async () => {
              try {
                const slug = project.slug || project.name
                await authFetch(apiUrl('/api/projects/snapshot'), {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ slug, port: devPort }),
                })
              } catch {}
            }, 3000)
          }}
          style={{ width: '100%', height: '100%', border: 'none', borderRadius: 8, background: '#fff' }}
        />
      </div>
    )
  }

  // For static HTML projects, show static preview
  if (isStatic) {
    const previewPath = project.name?.endsWith('.html') ? project.name : (project.slug || project.name) + '/'
    const mode = ''
    return <div style={{ flex: 1 }}><iframe key={refreshKey} src={`${api}/api/files/preview/${previewPath}?token=${token}${mode}`} sandbox="allow-scripts allow-same-origin" style={{ width: '100%', height: '100%', border: 'none', borderRadius: 8, background: '#fff' }} /></div>
  }

  const startDevServer = async () => {
    setStarting(true)
    setError('')
    try {
      // Kill any existing dev server first by killing the terminal
      await authFetch(apiUrl('/api/terminal/kill'), { method: 'POST' }).catch(() => {})
      await new Promise(r => setTimeout(r, 1500))

      // Start dev server for THIS project (fresh terminal starts in workspace root)
      const slug = project.slug || project.name
      let projectPath = slug

      // Cloud project: restore from DB to .cloud-cache first
      const hasLocalFolder = project.framework !== null
      if (!hasLocalFolder) {
        await authFetch(apiUrl('/api/files/restore-cloud'), {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ slug }),
        }).catch(() => {})
        projectPath = `.cloud-cache/${slug}`
      }

      const cmd = `cd ${projectPath} && npm install && npx --yes concurrently "npx vite --host --port 5175" "node server/index.cjs 2>nul || node server/index.js 2>nul"`
      await authFetch(apiUrl('/api/terminal/run'), {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ command: cmd }),
      })

      // Poll for dev server to be ready
      let attempts = 0
      const poll = setInterval(async () => {
        attempts++
        if (attempts > 30) { clearInterval(poll); setStarting(false); setError('Dev server failed to start. Check project for errors.'); return }
        try {
          const r = await authFetch(apiUrl(`/api/devserver/status?_=${Date.now()}`))
          const s = await r.json()
          // Only accept port 5175 (our Vite frontend), not backend ports
          if (s.port && s.port !== 3001 && s.port !== 5173) { setDevPort(s.port); setStarting(false); clearInterval(poll); onServerStarted?.(project.slug || project.name, s.port) }
        } catch {}
      }, 3000)
    } catch {
      setStarting(false)
      setError('Failed to start dev server')
    }
  }

  return (
    <div style={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', flexDirection: 'column', gap: 12 }}>
      <span style={{ fontSize: 36 }}>{project.framework === 'react' ? '⚛️' : project.framework === 'vue' ? '💚' : '▲'}</span>
      <span style={{ fontSize: 13, color: 'rgba(255,255,255,0.5)' }}>{project.framework} project</span>
      {error && <span style={{ fontSize: 11, color: '#f07070' }}>{error}</span>}
      {starting ? (
        <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 12 }}>
          <div style={{ position: 'relative', width: 40, height: 40 }}>
            <div style={{ position: 'absolute', inset: 0, border: '3px solid rgba(255,255,255,0.1)', borderTopColor: '#5fd4a4', borderRadius: '50%', animation: 'pulse-spin 0.8s linear infinite' }} />
            <div style={{ position: 'absolute', inset: 5, border: '3px solid rgba(255,255,255,0.1)', borderBottomColor: '#7c6ef5', borderRadius: '50%', animation: 'pulse-spin 1.2s linear infinite reverse' }} />
          </div>
          <span style={{ fontSize: 12, fontFamily: 'ui-monospace, monospace', color: '#5fd4a4', fontWeight: 700 }}>Starting {project.name}...</span>
          <span style={{ fontSize: 10, fontFamily: 'ui-monospace, monospace', color: 'rgba(255,255,255,0.4)' }}>Installing deps & launching server</span>
          <style>{`@keyframes pulse-spin { to { transform: rotate(360deg) } }`}</style>
        </div>
      ) : (
        <button onClick={startDevServer} style={{ padding: '10px 24px', borderRadius: 8, border: '1px solid rgba(95,212,164,0.4)', background: 'rgba(95,212,164,0.1)', color: '#5fd4a4', fontWeight: 700, fontSize: 12, cursor: 'pointer', fontFamily: 'ui-monospace, monospace' }}>
          ▶ Run Dev Server
        </button>
      )}
    </div>
  )
}

type DashboardView = 'home' | 'projects' | 'starred' | 'shared' | 'published' | 'agents' | 'templates' | 'deployments' | 'database' | 'secrets' | 'integrations' | 'analytics' | 'cron' | 'security' | 'community' | 'billing' | 'settings' | 'components' | 'marketplace' | 'packages' | 'cicd' | 'debugger' | 'teams' | 'comments' | 'versions' | 'review' | 'docs' | 'themes' | 'history' | 'admin' | 'support'
type ProjectView = 'chat' | 'preview' | null

const SIDEBAR: { section?: string; items: { id: DashboardView; icon: string; label: string }[] }[] = [
  { items: [
    { id: 'home', icon: '🏠', label: 'Home' },
    { id: 'projects', icon: '📁', label: 'Projects' },
    { id: 'starred', icon: '⭐', label: 'Starred' },
    { id: 'shared', icon: '🔗', label: 'Shared with you' },
    { id: 'published', icon: '🌐', label: 'Published' },
    { id: 'history', icon: '💬', label: 'Chat History' },
  ]},
  { section: 'Build', items: [
    { id: 'agents', icon: '🤖', label: 'Agents' },
    { id: 'templates', icon: '⚡', label: 'Templates' },
    { id: 'components', icon: '🧩', label: 'Components' },
    { id: 'community', icon: '🌍', label: 'Community' },
    { id: 'marketplace', icon: '📥', label: 'Extensions' },
  ]},
  { section: 'Tools', items: [
    { id: 'deployments', icon: '🚀', label: 'Deployments' },
    { id: 'database', icon: '🗄', label: 'Database' },
    { id: 'secrets', icon: '🔒', label: 'Secrets' },
    { id: 'packages', icon: '📦', label: 'Packages' },
    { id: 'integrations', icon: '🔌', label: 'Integrations' },
    { id: 'cicd', icon: '⚙', label: 'CI/CD' },
    { id: 'debugger', icon: '🐞', label: 'Debugger' },
  ]},
  { section: 'Collab', items: [
    { id: 'teams', icon: '👥', label: 'Teams' },
    { id: 'comments', icon: '💬', label: 'Comments' },
    { id: 'versions', icon: '🕓', label: 'Version History' },
  ]},
  { section: 'Monitor', items: [
    { id: 'analytics', icon: '📊', label: 'Analytics' },
    { id: 'cron', icon: '⏰', label: 'Automations' },
    { id: 'security', icon: '🛡', label: 'Security' },
  ]},
  { section: 'More', items: [
    { id: 'review', icon: '🔍', label: 'Code Review' },
    { id: 'docs', icon: '📖', label: 'Generate Docs' },
    { id: 'themes', icon: '🎨', label: 'Themes' },
    { id: 'billing', icon: 'card', label: 'Billing' },
    { id: 'admin', icon: '🛡️', label: 'Admin' },
    { id: 'support', icon: '📩', label: 'Support' },
    { id: 'settings', icon: 'gear', label: 'Settings' },
  ]},
]

const ICON_MAP: Record<string, string> = {
  home: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1',
  folder: 'M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-6l-2-2H5a2 2 0 00-2 2z',
  star: 'M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z',
  share: 'M8.684 13.342C8.886 12.938 9 12.482 9 12c0-.482-.114-.938-.316-1.342m0 2.684a3 3 0 110-2.684m0 2.684l6.632 3.316m-6.632-6l6.632-3.316m0 0a3 3 0 105.367-2.684 3 3 0 00-5.367 2.684zm0 9.316a3 3 0 105.368 2.684 3 3 0 00-5.368-2.684z',
  globe: 'M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9',
  bot: 'M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z',
  layout: 'M4 5a1 1 0 011-1h14a1 1 0 011 1v2a1 1 0 01-1 1H5a1 1 0 01-1-1V5zM4 13a1 1 0 011-1h6a1 1 0 011 1v6a1 1 0 01-1 1H5a1 1 0 01-1-1v-6zM16 13a1 1 0 011-1h2a1 1 0 011 1v6a1 1 0 01-1 1h-2a1 1 0 01-1-1v-6z',
  users: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0z',
  rocket: 'M13 10V3L4 14h7v7l9-11h-7z',
  database: 'M4 7v10c0 2.21 3.582 4 8 4s8-1.79 8-4V7M4 7c0 2.21 3.582 4 8 4s8-1.79 8-4M4 7c0-2.21 3.582-4 8-4s8 1.79 8 4',
  lock: 'M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z',
  plug: 'M13 10V3L4 14h7v7l9-11h-7z',
  chart: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z',
  clock: 'M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z',
  shield: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z',
  card: 'M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z',
  gear: 'M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.066 2.573c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.573 1.066c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.066-2.573c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z M15 12a3 3 0 11-6 0 3 3 0 016 0z',
  chat: 'M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z',
}

function SvgIcon({ name, size = 16 }: { name: string; size?: number }) {
  const d = ICON_MAP[name]
  if (!d) return <span style={{ width: size, height: size, display: 'inline-block' }} />
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
      <path d={d} />
    </svg>
  )
}

const TEMPLATES = [
  { name: 'SaaS Dashboard', desc: 'Charts, auth, dark mode', color: '#7c6ef5' },
  { name: 'E-commerce', desc: 'Cart, checkout, payments', color: '#f5a157' },
  { name: 'Landing Page', desc: 'Hero, pricing, CTA', color: '#5fd4a4' },
  { name: 'AI Agent', desc: 'Chat, tools, memory', color: '#f07070' },
  { name: 'CRM', desc: 'Contacts, deals, pipeline', color: '#ffd086' },
  { name: 'Portfolio', desc: 'Projects, about, contact', color: '#38bdf8' },
]


function formatDashChat(text: string): string {
  return text
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/```(\w*)\n([\s\S]*?)```/g, '<pre style="background:rgba(255,255,255,0.05);border:1px solid rgba(255,255,255,0.1);border-radius:6px;padding:10px;font-family:ui-monospace,monospace;font-size:11px;margin:8px 0;overflow-x:auto">$2</pre>')
    .replace(/`([^`]+)`/g, '<code style="background:rgba(255,255,255,0.08);padding:1px 5px;border-radius:3px;font-family:ui-monospace,monospace;font-size:11px">$1</code>')
    .replace(/\*\*(.+?)\*\*/g, '<strong style="color:#fff">$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/^\d+\.\s+(.+)$/gm, '<div style="padding-left:12px;margin:4px 0"><strong style="color:rgba(255,255,255,0.9)">$&</strong></div>')
    .replace(/^\s+\*\s+(.+)$/gm, '<div style="padding-left:24px;margin:2px 0">\u2022 $1</div>')
    .replace(/^\*\s+(.+)$/gm, '<div style="padding-left:12px;margin:2px 0">\u2022 $1</div>')
    .replace(/\n/g, '<br/>')
}


export function DashboardPage({ onOpenIDE, onOpenProject }: {
  onOpenIDE: (prompt?: string) => void
  onOpenProject?: (projectId: string) => void
}) {
  const user = useAuthStore(s => s.user)
  const token = useAuthStore(s => s.token)
  const logout = useAuthStore(s => s.logout)
  const API = (import.meta as any).env?.VITE_API_URL ?? 'http://localhost:3001'
  const [view, setView] = useState<DashboardView>('home')
  const [projects, setProjects] = useState<Project[]>([])
  const [input, setInput] = useState('')
  const [chatMessages, setChatMessages] = useState<{ role: string; text: string }[]>([])
  const [chatLoading, setChatLoading] = useState(false)
  const [activeProject, setActiveProject] = useState<Project | null>(null)
  const [projectView, setProjectView] = useState<ProjectView>(null)
  const [devPort, setDevPort] = useState<number | null>(null)
  const [runningProject, setRunningProject] = useState<string | null>(null)
  const containerRef = useRef<HTMLDivElement>(null)
  const chatEndRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLTextAreaElement>(null)

  useEffect(() => {
    const loadProjects = () => {
      authFetch(apiUrl('/api/projects')).then(r => {
        if (!r.ok) { console.error('[Dashboard] /api/projects failed:', r.status); return null }
        return r.json()
      }).then(d => {
        if (d) {
          const list = Array.isArray(d) ? d : d.projects || []
          console.log('[Dashboard] loaded', list.length, 'projects')
          setProjects(list)
        }
      }).catch(e => console.error('[Dashboard] projects fetch error:', e))
    }
    loadProjects()
    // Refresh when agent creates a project
    const handler = () => loadProjects()
    window.addEventListener('pulse:projects-changed', handler)
    return () => window.removeEventListener('pulse:projects-changed', handler)
    // Check if dev server is already running
    authFetch(apiUrl('/api/devserver/status')).then(r => r.json()).then(s => {
      if (s.port) setDevPort(s.port)
    }).catch(() => {})
  }, [])

  useEffect(() => { chatEndRef.current?.scrollIntoView({ behavior: 'smooth' }) }, [chatMessages])

  const onPointerMove = useCallback((e: React.PointerEvent) => {
    containerRef.current?.style.setProperty('--mx', `${e.clientX}px`)
    containerRef.current?.style.setProperty('--my', `${e.clientY}px`)
  }, [])

  const handleSend = async () => {
    const text = input.trim()
    if (!text || chatLoading) return
    setInput('')
    setChatMessages(prev => [...prev, { role: 'user', text }])
    setChatLoading(true)
    try {
      // Use generate endpoint to create a real project
      const res = await authFetch(apiUrl('/api/generate'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ prompt: text }),
      })

      // Parse SSE stream
      const reader = res.body?.getReader()
      const decoder = new TextDecoder()
      let projectName = ''
      let fileCount = 0

      if (reader) {
        while (true) {
          const { done, value } = await reader.read()
          if (done) break
          const chunk = decoder.decode(value, { stream: true })
          const lines = chunk.split('\n')
          for (const line of lines) {
            if (!line.startsWith('data: ')) continue
            try {
              const data = JSON.parse(line.slice(6))
              if (data.type === 'status') {
                setChatMessages(prev => {
                  const updated = [...prev]
                  const last = updated[updated.length - 1]
                  if (last?.role === 'ai') { last.text = `⏳ ${data.text}` }
                  else { updated.push({ role: 'ai', text: `⏳ ${data.text}` }) }
                  return [...updated]
                })
              }
              if (data.type === 'file') {
                fileCount++
                setChatMessages(prev => {
                  const updated = [...prev]
                  const last = updated[updated.length - 1]
                  if (last?.role === 'ai') { last.text = `📦 Creating files... (${fileCount} created)\n└─ ${data.path}` }
                  else { updated.push({ role: 'ai', text: `📦 ${data.path}` }) }
                  return [...updated]
                })
              }
              if (data.type === 'done') { projectName = data.project }
              if (data.type === 'error') {
                setChatMessages(prev => [...prev.filter(m => m.role !== 'ai' || !m.text.startsWith('⏳')), { role: 'ai', text: `❌ Error: ${data.text}` }])
              }
            } catch {}
          }
        }
      }

      if (projectName) {
        setChatMessages(prev => {
          const filtered = prev.filter(m => !(m.role === 'ai' && (m.text.startsWith('⏳') || m.text.startsWith('📦'))))
          return [...filtered, { role: 'ai', text: `✅ Project "${projectName}" created with ${fileCount} files!\n\nClick it below to preview.` }]
        })
        // Refresh project list
        const projRes = await authFetch(apiUrl('/api/projects'))
        const projData = await projRes.json()
        setProjects(Array.isArray(projData) ? projData : projData.projects || [])
      }
    } catch {
      setChatMessages(prev => [...prev, { role: 'ai', text: 'Error: Could not generate app' }])
    }
    setChatLoading(false)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSend() }
  }

  const handleStarProject = async (p: Project) => {
    try {
      const res = await authFetch(apiUrl(`/api/projects/${p.id}/star`), { method: 'POST' })
      const data = await res.json()
      if (data.ok) {
        setProjects(prev => prev.map(x => x.id === p.id ? { ...x, starred: data.starred } : x))
      }
    } catch {}
  }

  const handleDeleteProject = async (p: Project) => {
    const confirm = window.prompt(`Type "delete" to permanently delete "${p.name}":`)
    if (confirm?.toLowerCase() !== 'delete') return
    try {
      await authFetch(apiUrl(`/api/projects/${p.id}`), { method: 'DELETE' })
      // Also delete synced files
      await authFetch(apiUrl(`/api/sync/${p.slug || p.name}`), { method: 'DELETE' }).catch(() => {})
      setProjects(prev => prev.filter(x => x.id !== p.id))
    } catch {}
  }

  // Load project chat history when activeProject changes (same as IDE does)
  const [projectChatMessages, setProjectChatMessages] = useState<{ role: string; content: string }[]>([])
  const [projectChatLoading, setProjectChatLoading] = useState(false)
  const projectChatEndRef = useRef<HTMLDivElement>(null)
  const [projectChatInput, setProjectChatInput] = useState('')
  const [projectChatMode, setProjectChatMode] = useState<'ask' | 'agent'>('ask')
  const [dashTabs, setDashTabs] = useState<Array<{ id: string; messages: any[]; convId: string | null }>>([{ id: 'default', messages: [], convId: null }])
  const [dashActiveTab, setDashActiveTab] = useState(0)
  const [projectAgentMode, setProjectAgentMode] = useState<'safe' | 'assisted' | 'autonomous'>('assisted')
  const [projectConvId, setProjectConvId] = useState<string | null>(null)

  // Persist a message to the conversation history
  const persistProjectMessage = async (role: string, content: string) => {
    if (!activeProject) return
    let id = projectConvId
    if (!id) {
      try {
        const slug = activeProject.slug || activeProject.name
        const res = await authFetch(apiUrl('/api/history'), {
          method: 'POST', headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ title: content.slice(0, 60), mode: projectChatMode, model: 'auto', project: slug }),
        })
        const data = await res.json()
        id = data.id
        setProjectConvId(id)
      } catch { return }
    }
    authFetch(apiUrl(`/api/history/${id}/messages`), {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ role, content }),
    }).catch(() => {})
  }

  useEffect(() => {
    if (!activeProject) { setProjectChatMessages([]); setProjectConvId(null); return }
    const slug = activeProject.slug || activeProject.name
    const name = activeProject.name
    const tryLoad = async () => {
      // First check localStorage for saved tabs (same as IDE)
      const savedKey = `pulse-chat-tabs-${slug}`
      const saved = localStorage.getItem(savedKey)
      if (saved) {
        try {
          const tabs = JSON.parse(saved)
          if (Array.isArray(tabs) && tabs.length > 0) {
            setDashTabs(tabs.map((t: any) => ({ id: t.id || crypto.randomUUID(), messages: t.messages || [], convId: t.convId })))
            setDashActiveTab(0)
            setProjectChatMessages(tabs[0].messages?.map((m: any) => ({ role: m.role, content: m.content })) || [])
            setProjectConvId(tabs[0].convId)
            return
          }
        } catch {}
      }
      // Fallback: load from API
      let res = await authFetch(apiUrl(`/api/history?project=${encodeURIComponent(slug)}`))
      let list = res.ok ? await res.json() : []
      list = Array.isArray(list) ? list : []
      if (list.length === 0 && name !== slug) {
        res = await authFetch(apiUrl(`/api/history?project=${encodeURIComponent(name)}`))
        list = res.ok ? await res.json() : []
        list = Array.isArray(list) ? list : []
      }
      if (list.length === 0) {
        const alt = name.endsWith('.html') ? name.replace('.html', '') : name + '.html'
        res = await authFetch(apiUrl(`/api/history?project=${encodeURIComponent(alt)}`))
        list = res.ok ? await res.json() : []
        list = Array.isArray(list) ? list : []
      }
      if (list.length > 0) {
        // Load all conversations merged (oldest first)
        const allMessages: any[] = []
        for (const conv of [...list].reverse()) {
          try {
            const convRes = await authFetch(apiUrl(`/api/history/${conv.id}`))
            const detail = await convRes.json()
            if (detail?.messages) {
              allMessages.push(...detail.messages.map((m: any) => ({ role: m.role, content: m.content })))
            }
          } catch {}
        }
        const mainTab = { id: crypto.randomUUID(), messages: allMessages, convId: list[0].id }
        const newTab = { id: crypto.randomUUID(), messages: [] as any[], convId: null }
        setDashTabs([mainTab, newTab])
        setDashActiveTab(0)
        setProjectChatMessages(allMessages)
        setProjectConvId(list[0].id)
        return
      }
      setProjectChatMessages([])
    }
    tryLoad().catch(() => setProjectChatMessages([]))
  }, [activeProject])

  useEffect(() => { projectChatEndRef.current?.scrollIntoView({ behavior: 'smooth' }) }, [projectChatMessages])

  const handleProjectChatSend = async () => {
    const text = projectChatInput.trim()
    if (!text || projectChatLoading || !activeProject) return
    setProjectChatInput('')
    setProjectChatMessages(prev => [...prev, { role: 'user', content: text }])
    persistProjectMessage('user', text)

    // Agent mode: handle stream directly (same as IDE's AIPanel does)
    if (projectChatMode === 'agent') {
      localStorage.setItem('pulse-agent-mode', projectAgentMode)
      let slug = activeProject.slug || activeProject.name
      // Cloud projects live in .cloud-cache/<slug>
      if ((activeProject as any).storage === 'cloud') slug = `.cloud-cache/${slug}`
      setProjectChatLoading(true)
      setProjectChatMessages(prev => [...prev, { role: 'assistant', content: '⏳ Agent working...' }])
      try {
        const res = await authFetch(apiUrl('/api/agent-stream'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ goal: text, projectName: slug, agentMode: projectAgentMode }),
        })
        const reader = res.body?.getReader()
        const decoder = new TextDecoder()
        let reply = ''
        let runId = ''
        const update = (t: string) => setProjectChatMessages(prev => { const u = [...prev]; u[u.length - 1] = { role: 'assistant', content: t || '⏳ Agent working...' }; return [...u] })
        if (reader) {
          while (true) {
            const { done, value } = await reader.read()
            if (done) break
            const chunk = decoder.decode(value, { stream: true })
            for (const line of chunk.split('\n')) {
              if (!line.startsWith('data: ')) continue
              try {
                const d = JSON.parse(line.slice(6))
                if (d.type === 'ping') { runId = d.runId || ''; continue }
                if (d.type === 'phase_complete' || d.type === 'done') continue
                if (d.type === 'phase') {
                  if (d.phase === 'applying') { reply += '\n⚙️ Applying changes...\n'; update(reply) }
                  continue
                }
                if (d.type === 'command_output') continue
                if (d.type === 'approval_required' || d.type === 'tool_request') {
                  await authFetch(apiUrl('/api/agent-stream'), { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ approveRunId: runId }) }).catch(() => {})
                  continue
                }
                if (d.type === 'file_patched') {
                  reply += `✅ Patched: ${d.path}\n`; update(reply)
                  // Refresh preview
                  window.dispatchEvent(new CustomEvent('pulse:preview-refresh'))
                  continue
                }
                if (d.type === 'text' && d.text) { reply += d.text; update(reply); continue }
                if (d.text && !d.type) { reply += d.text; update(reply); continue }
                if (d.type === 'summary' && d.filesModified?.length) {
                  reply += `\n✅ Modified: ${d.filesModified.join(', ')}\n`; update(reply)
                  continue
                }
                if (d.type === 'error') { reply += `❌ ${d.message || d.text || 'Error'}\n`; update(reply); continue }
                if (d.type === 'diff') {
                  reply += (d.diff || '') + '\n'; update(reply)
                  continue
                }
              } catch {}
            }
          }
        }
        const final = reply.trim() || '✅ Done'
        setProjectChatMessages(prev => { const u = [...prev]; u[u.length - 1] = { role: 'assistant', content: final }; return [...u] })
        persistProjectMessage('assistant', final)
      } catch {
        setProjectChatMessages(prev => { const u = [...prev]; u[u.length - 1] = { role: 'assistant', content: '❌ Agent error' }; return [...u] })
      }
      setProjectChatLoading(false)
      return
    }

    // Ask mode: stream from /api/chat/stream
    setProjectChatLoading(true)
    setProjectChatMessages(prev => [...prev, { role: 'assistant', content: '⏳ Thinking...' }])
    try {
      const slug = activeProject.slug || activeProject.name
      const res = await authFetch(apiUrl('/api/chat/stream'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ messages: [{ role: 'user', content: text }], context: { mode: 'edit', model: 'auto', files: [], project: slug } }),
      })
      const reader = res.body?.getReader()
      const decoder = new TextDecoder()
      let reply = ''
      if (reader) {
        while (true) {
          const { done, value } = await reader.read()
          if (done) break
          const chunk = decoder.decode(value, { stream: true })
          for (const line of chunk.split('\n')) {
            if (!line.startsWith('data: ')) continue
            try {
              const d = JSON.parse(line.slice(6))
              if (d.ping) continue
              if (d.text) { reply += d.text; setProjectChatMessages(prev => { const u = [...prev]; u[u.length - 1] = { role: 'assistant', content: reply }; return [...u] }) }
              if (d.done) break
            } catch {}
          }
        }
      }
      const finalContent = reply.trim() || '✅ Done'
      setProjectChatMessages(prev => { const u = [...prev]; u[u.length - 1] = { role: 'assistant', content: finalContent }; return [...u] })
      persistProjectMessage('assistant', finalContent)
      // Auto-apply FILE: blocks
      const codeBlocks = finalContent.match(/```\w*\n([\s\S]*?)```/g)
      if (codeBlocks) {
        for (const block of codeBlocks) {
          const code = block.replace(/^```\w*\n?/, '').replace(/\n?```$/, '')
          const fileMatch = code.match(/^\/\/\s*FILE:\s*(.+)/i)
          if (fileMatch) {
            const filePath = fileMatch[1].trim().replace(/^[./\\]+/, '')
            const content = code.split('\n').slice(1).join('\n')
            await authFetch(apiUrl('/api/files/write'), { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ path: filePath, content }) }).catch(() => {})
          }
        }
        window.dispatchEvent(new CustomEvent('pulse:files-changed'))
      }
    } catch {
      setProjectChatMessages(prev => { const u = [...prev]; u[u.length - 1] = { role: 'assistant', content: '❌ Error' }; return [...u] })
    }
    setProjectChatLoading(false)
  }

  // + dropdown features for the preview panel
  type PreviewFeature = null | 'database' | 'secrets' | 'deploy' | 'analytics' | 'cron' | 'templates' | 'integrations' | 'cicd' | 'packages' | 'security' | 'debugger' | 'components' | 'marketplace'
  const PREVIEW_FEATURES: { id: PreviewFeature; icon: string; label: string }[] = [
    { id: 'database', icon: '🗄', label: 'Database' },
    { id: 'secrets', icon: '🔒', label: 'Secrets' },
    { id: 'deploy', icon: '🚀', label: 'Deploy' },
    { id: 'analytics', icon: '📊', label: 'Analytics' },
    { id: 'cron', icon: '⏰', label: 'Automations' },
    { id: 'templates', icon: '⚡', label: 'Templates' },
    { id: 'integrations', icon: '🔌', label: 'Integrations' },
    { id: 'cicd', icon: '⚙', label: 'CI/CD' },
    { id: 'packages', icon: '📦', label: 'Packages' },
    { id: 'security', icon: '🛡', label: 'Security' },
    { id: 'debugger', icon: '🐞', label: 'Debugger' },
    { id: 'components', icon: '🧩', label: 'Components' },
    { id: 'marketplace', icon: '📥', label: 'Extensions' },
  ]
  const [previewFeature, setPreviewFeature] = useState<PreviewFeature>(null)
  const [previewPlusOpen, setPreviewPlusOpen] = useState(false)

  useEffect(() => {
    if (!previewPlusOpen) return
    const close = () => setPreviewPlusOpen(false)
    document.addEventListener('click', close)
    return () => document.removeEventListener('click', close)
  }, [previewPlusOpen])

  const renderPreviewFeature = () => {
    const slug = activeProject?.slug || activeProject?.name || ''
    const projectId = activeProject?.id || ''
    switch (previewFeature) {
      case 'database': return <DatabasePanel projectId={projectId} projectSlug={slug} />
      case 'secrets': return <SecretsPanel projectSlug={slug} />
      case 'deploy': return <DeployPanel projectSlug={slug} />
      case 'analytics': return <AnalyticsDashboard />
      case 'cron': return <CronPanel />
      case 'templates': return <TemplatesPanel onCreated={() => setPreviewFeature(null)} />
      case 'integrations': return <IntegrationsPanel />
      case 'cicd': return <CICDPanel />
      case 'packages': return <PackagePanel />
      case 'security': return <SecurityScannerPanel onClose={() => setPreviewFeature(null)} />
      case 'debugger': return <DebuggerPanel />
      case 'components': return <ComponentLibraryPanel />
      case 'marketplace': return <MarketplacePanel />
      default: return null
    }
  }

  // Project split view: chat (left) + preview (right) — rendered inside main area
  const renderSplitView = () => {
    if (!activeProject) return null
    return (
      <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
        {/* Top bar */}
        <div style={{ display: 'flex', alignItems: 'center', padding: '12px 20px', borderBottom: '1px solid rgba(255,255,255,0.06)', gap: 12 }}>
          <button onClick={() => { authFetch(apiUrl('/api/terminal/kill'), { method: 'POST' }).catch(() => {}); setActiveProject(null); setProjectView(null); setPreviewFeature(null) }} style={backBtnStyle}>← Back</button>
          <span style={{ fontWeight: 700, fontSize: 14, color: '#fff' }}>{activeProject.name}</span>
          <div style={{ flex: 1 }} />
          <button onClick={() => onOpenProject?.(activeProject.id)} style={tabStyle}>Open Code</button>
        </div>
        {/* Split: Chat left + Preview right */}
        <div style={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
          {/* Chat panel (left) */}
          <div style={{ width: '30%', minWidth: 240, maxWidth: 360, borderRight: '1px solid rgba(255,255,255,0.06)', display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '10px 16px', borderBottom: '1px solid rgba(255,255,255,0.06)', fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.5)', fontFamily: 'ui-monospace, monospace' }}>💬 Chat History</div>
            {/* Chat Tabs */}
            <div style={{ display: 'flex', alignItems: 'center', borderBottom: '1px solid rgba(255,255,255,0.06)', overflowX: 'auto', flexShrink: 0 }}>
              {dashTabs.map((tab, idx) => (
                <div
                  key={tab.id}
                  onClick={() => {
                    setDashTabs(prev => { const u = [...prev]; u[dashActiveTab] = { ...u[dashActiveTab], messages: [...projectChatMessages] }; return u })
                    setDashActiveTab(idx)
                    setProjectChatMessages(dashTabs[idx].messages.map((m: any) => ({ role: m.role, content: m.content })))
                    setProjectConvId(dashTabs[idx].convId)
                  }}
                  style={{ padding: '6px 12px', cursor: 'pointer', fontSize: 10, fontFamily: 'ui-monospace, monospace', fontWeight: 600, color: idx === dashActiveTab ? '#fff' : 'rgba(255,255,255,0.4)', borderBottom: idx === dashActiveTab ? '2px solid #ffd086' : '2px solid transparent', whiteSpace: 'nowrap' }}
                >Chat {idx + 1}</div>
              ))}
              <button
                onClick={() => {
                  setDashTabs(prev => { const u = [...prev]; u[dashActiveTab] = { ...u[dashActiveTab], messages: [...projectChatMessages] }; return [...u, { id: crypto.randomUUID(), messages: [], convId: null }] })
                  setDashActiveTab(dashTabs.length)
                  setProjectChatMessages([])
                  setProjectConvId(null)
                }}
                style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.4)', cursor: 'pointer', fontSize: 14, padding: '4px 10px', flexShrink: 0 }}
              >+</button>
            </div>
            <div style={{ flex: 1, overflow: 'auto', padding: 12 }}>
              {projectChatMessages.length === 0 && <div style={{ color: 'rgba(255,255,255,0.3)', fontSize: 12, textAlign: 'center', padding: 40 }}>No chat history for this project yet.</div>}
              {projectChatMessages.map((m, i) => (
                <div key={i} style={{ margin: '8px 0', padding: '10px 14px', borderRadius: 10, background: m.role === 'user' ? 'rgba(255,208,134,0.08)' : 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', color: '#eee', fontSize: 13, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
                  <span style={{ fontSize: 9, fontWeight: 700, color: m.role === 'user' ? '#ffd086' : '#5fd4a4', display: 'block', marginBottom: 3 }}>{m.role === 'user' ? 'You' : 'PULSE AI'}</span>
                  {m.role === 'user' ? m.content : <span dangerouslySetInnerHTML={{ __html: formatDashChat(m.content) }} />}
                </div>
              ))}
              {projectChatLoading && !projectChatMessages.some(m => m.content.startsWith('⏳')) && <div style={{ opacity: 0.5, padding: 10, fontSize: 13 }}>Thinking...</div>}
              <div ref={projectChatEndRef} />
            </div>
            <div style={{ padding: 10, borderTop: '1px solid rgba(255,255,255,0.06)', display: 'flex', flexDirection: 'column', gap: 6 }}>
              {/* Mode selector: ask / agent + safe dropdown */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <button onClick={() => setProjectChatMode('ask')} style={{ padding: '3px 10px', borderRadius: 5, border: projectChatMode === 'ask' ? '1px solid #ffd086' : '1px solid rgba(255,255,255,0.08)', background: projectChatMode === 'ask' ? 'rgba(255,208,134,0.1)' : 'none', color: projectChatMode === 'ask' ? '#ffd086' : 'rgba(255,255,255,0.4)', fontSize: 10, cursor: 'pointer', fontFamily: 'ui-monospace, monospace', fontWeight: 700 }}>ask</button>
                <button onClick={() => setProjectChatMode('agent')} style={{ padding: '3px 10px', borderRadius: 5, border: projectChatMode === 'agent' ? '1px solid #5fd4a4' : '1px solid rgba(255,255,255,0.08)', background: projectChatMode === 'agent' ? 'rgba(95,212,164,0.1)' : 'none', color: projectChatMode === 'agent' ? '#5fd4a4' : 'rgba(255,255,255,0.4)', fontSize: 10, cursor: 'pointer', fontFamily: 'ui-monospace, monospace', fontWeight: 700 }}>agent</button>
                {projectChatMode === 'agent' && (
                  <select value={projectAgentMode} onChange={e => setProjectAgentMode(e.target.value as any)} style={{ padding: '2px 6px', borderRadius: 4, border: '1px solid rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.03)', color: 'rgba(255,255,255,0.6)', fontSize: 9, fontFamily: 'ui-monospace, monospace', cursor: 'pointer', outline: 'none' }}>
                    <option value="safe">🔒 Safe</option>
                    <option value="assisted">🤝 Assisted</option>
                    <option value="autonomous">⚡ Auto</option>
                  </select>
                )}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <textarea value={projectChatInput} onChange={e => setProjectChatInput(e.target.value)} onKeyDown={e => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleProjectChatSend() } }} placeholder={projectChatMode === 'ask' ? 'Ask about this project...' : 'Describe the task...'} style={chatInputStyle} rows={1} />
                <button onClick={handleProjectChatSend} style={sendBtnStyle}>Send</button>
              </div>
            </div>
          </div>
          {/* Preview panel (right) */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
            <div style={{ padding: '10px 16px', borderBottom: '1px solid rgba(255,255,255,0.06)', fontSize: 11, fontWeight: 700, color: 'rgba(255,255,255,0.5)', fontFamily: 'ui-monospace, monospace', display: 'flex', alignItems: 'center', gap: 8 }}>
              {previewFeature ? (
                <>
                  <button onClick={() => setPreviewFeature(null)} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.5)', cursor: 'pointer', fontSize: 11, fontFamily: 'ui-monospace, monospace' }}>← Preview</button>
                  <span style={{ color: '#fff' }}>{PREVIEW_FEATURES.find(f => f.id === previewFeature)?.icon} {PREVIEW_FEATURES.find(f => f.id === previewFeature)?.label}</span>
                </>
              ) : (
                <span>👁 Preview</span>
              )}
              <div style={{ flex: 1 }} />
              {/* + dropdown */}
              <div style={{ position: 'relative' }}>
                <button onClick={e => { e.stopPropagation(); setPreviewPlusOpen(!previewPlusOpen) }} style={{ width: 24, height: 24, borderRadius: 6, border: '1px solid rgba(255,255,255,0.12)', background: 'rgba(255,255,255,0.04)', color: '#fff', fontSize: 14, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', lineHeight: 1 }}>+</button>
                {previewPlusOpen && (
                  <div style={{ position: 'absolute', top: 30, right: 0, background: 'rgba(20,20,30,0.98)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 10, padding: 6, minWidth: 170, zIndex: 100, boxShadow: '0 12px 40px rgba(0,0,0,0.6)' }}>
                    {PREVIEW_FEATURES.map(f => (
                      <button key={f.id} onClick={() => { setPreviewFeature(f.id); setPreviewPlusOpen(false) }} style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '7px 10px', border: 'none', background: 'none', color: 'rgba(255,255,255,0.7)', fontSize: 11, cursor: 'pointer', borderRadius: 5, textAlign: 'left' }}
                        onMouseEnter={e => (e.currentTarget.style.background = 'rgba(255,255,255,0.06)')}
                        onMouseLeave={e => (e.currentTarget.style.background = 'none')}>
                        <span>{f.icon}</span><span>{f.label}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
            {/* Feature panel or Preview */}
            {previewFeature ? (
              <div style={{ flex: 1, overflow: 'auto' }}>{renderPreviewFeature()}</div>
            ) : (
              <ProjectPreviewView project={activeProject} api={API} token={token} onServerStarted={(slug, port) => { setRunningProject(slug); setDevPort(port) }} />
            )}
          </div>
        </div>
      </div>
    )
  }

  // Render panel content based on view
  const renderContent = () => {
    // If a project is open in split view, show it
    if (projectView && activeProject) return renderSplitView()

    switch (view) {
      case 'database': return <DatabasePanel />
      case 'secrets': return <SecretsPanel />
      case 'deployments': return <DeployPanel />
      case 'analytics': return <AnalyticsDashboard />
      case 'security': return <SecurityScannerPanel onClose={() => setView('home')} />
      case 'cron': return <CronPanel />
      case 'community': return <CommunityPage />
      case 'billing': return <BillingDashboard />
      case 'templates': return <TemplatesPanel onCreated={() => setView('projects')} />
      case 'settings': return <div style={{ padding: 20 }}><SettingsPanel onClose={() => setView('home')} /></div>
      case 'admin': return <AdminDashboard />
      case 'support': return <SupportPanel onClose={() => setView('home')} />
      case 'components': return <ComponentLibraryPanel />
      case 'marketplace': return <MarketplacePanel />
      case 'packages': return <PackagePanel />
      case 'cicd': return <CICDPanel />
      case 'debugger': return <DebuggerPanel />
      case 'teams': return <TeamWorkspaces />
      case 'comments': return <CodeComments projectId="" filePath="" />
      case 'versions': return <VersionHistory />
      case 'review': return <CodeReviewPanel onClose={() => setView('home')} />
      case 'docs': return <DocGenPanel onClose={() => setView('home')} />
      case 'themes': return <ThemeMarketplace />
      case 'projects': return renderProjects(projects)
      case 'starred': return renderProjects(projects.filter(p => p.starred))
      case 'shared': return <SharedProjectsView />
      case 'history': return <ChatHistoryPanel />
      case 'published': return renderProjects(projects.filter(p => p.deploy_url))
      case 'agents': return renderAgents()
      case 'integrations': return <IntegrationsPanel />
      default: return renderHome()
    }
  }

  const renderProjects = (list: Project[]) => (
    <div style={{ padding: '30px 40px' }}>
      <h2 style={{ fontSize: 18, fontWeight: 700, color: '#fff', marginBottom: 20 }}>{view === 'starred' ? 'Starred' : view === 'published' ? 'Published' : 'All Projects'}</h2>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 14 }}>
        {list.map(p => renderProjectCard(p))}
        {list.length === 0 && <div style={{ color: 'rgba(255,255,255,0.3)', fontSize: 13 }}>No projects here yet.</div>}
      </div>
    </div>
  )

  const isStaticProject = (p: Project) => p.framework === 'html'

  const renderProjectCard = (p: Project) => (
    <div key={p.id} style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 12, overflow: 'hidden', transition: 'border-color 0.2s' }}
      onMouseEnter={e => (e.currentTarget.style.borderColor = 'rgba(255,208,134,0.2)')}
      onMouseLeave={e => (e.currentTarget.style.borderColor = 'rgba(255,255,255,0.06)')}>
      <div style={{ height: 180, background: 'rgba(255,255,255,0.015)', display: 'flex', alignItems: 'center', justifyContent: 'center', borderBottom: '1px solid rgba(255,255,255,0.04)', overflow: 'hidden' }}>
        {p.deploy_url ? (
          <iframe src={p.deploy_url} sandbox="allow-scripts allow-same-origin" style={{ width: '100%', height: '100%', border: 'none', pointerEvents: 'none', background: '#fff' }} tabIndex={-1} />
        ) : (() => {
          const slug = (p as any).slug || p.name
          // Root HTML files: serve directly by filename
          const isRootHtml = p.framework === 'html' && p.name?.endsWith('.html')
          const previewPath = isRootHtml ? p.name : (slug === 'workspace' ? '' : slug + '/')
          const mode = ''
          const url = `${API}/api/files/preview/${previewPath}?token=${token}${mode}`
          return <iframe src={url} sandbox="allow-scripts allow-same-origin" style={{ width: '100%', height: '100%', border: 'none', pointerEvents: 'none', background: '#fff' }} tabIndex={-1} />
        })()}
      </div>
      <div style={{ padding: '12px 14px' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
          <span style={{ fontWeight: 700, fontSize: 12, color: '#fff', flex: 1 }}>{p.name}</span>
          {(p as any).storage === 'cloud' && <span style={{ fontSize: 8, padding: '1px 5px', borderRadius: 4, background: 'rgba(124,110,245,0.15)', color: '#7c6ef5', fontWeight: 700 }}>CLOUD</span>}
          {(p as any).storage === 'local' && <span style={{ fontSize: 8, padding: '1px 5px', borderRadius: 4, background: 'rgba(255,208,134,0.15)', color: '#ffd086', fontWeight: 700 }}>LOCAL</span>}
          {(p as any).storage === 'both' && <span style={{ fontSize: 8, padding: '1px 5px', borderRadius: 4, background: 'rgba(95,212,164,0.15)', color: '#5fd4a4', fontWeight: 700 }}>SYNCED</span>}
          {p.deploy_url && <span style={{ fontSize: 8, padding: '1px 5px', borderRadius: 4, background: 'rgba(95,212,164,0.15)', color: '#5fd4a4', fontWeight: 700 }}>LIVE</span>}
        </div>
        <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)', fontFamily: 'ui-monospace, monospace', marginBottom: 10 }}>
          {p.framework && <span style={{ marginRight: 6 }}>{p.framework}</span>}
          {p.updated_at && new Date(p.updated_at).toLocaleDateString()}
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          <button onClick={() => handleStarProject(p)} style={{ ...cardBtnStyle, background: p.starred ? 'rgba(255,208,134,0.15)' : undefined, color: p.starred ? '#ffd086' : undefined }}>{p.starred ? '★' : '☆'}</button>
          <button onClick={() => { setActiveProject(p); setProjectView('preview') }} style={cardBtnStyle}>Preview</button>
          <button onClick={() => { setActiveProject(p); setProjectView('chat') }} style={cardBtnStyle}>Chat</button>
          <button onClick={() => onOpenProject?.(p.id)} style={{ ...cardBtnStyle, background: 'rgba(255,208,134,0.1)', color: '#ffd086', borderColor: 'rgba(255,208,134,0.2)' }}>Code</button>
          <button onClick={() => handleDeleteProject(p)} style={{ ...cardBtnStyle, background: 'rgba(240,112,112,0.1)', color: '#f07070', borderColor: 'rgba(240,112,112,0.2)' }}>Delete</button>
        </div>
      </div>
    </div>
  )

  const renderAgents = () => (
    <div style={{ padding: '30px 40px' }}>
      <h2 style={{ fontSize: 18, fontWeight: 700, color: '#fff', marginBottom: 8 }}>AI Agents</h2>
      <p style={{ color: 'rgba(255,255,255,0.4)', fontSize: 12, marginBottom: 20 }}>Choose an agent specialized for your task</p>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: 12, marginBottom: 40 }}>
        {[
          { name: 'Coder', desc: 'Writes & edits code', color: '#ffd086', icon: '⚡', cmd: '' },
          { name: 'Reviewer', desc: 'Reviews & finds bugs', color: '#f07070', icon: '🔍', cmd: '/review ' },
          { name: 'Architect', desc: 'Designs structure', color: '#7c6ef5', icon: '🏗', cmd: 'Design the architecture for ' },
          { name: 'Docs', desc: 'Writes documentation', color: '#5fd4a4', icon: '📖', cmd: '/docs ' },
          { name: 'Tester', desc: 'Generates tests', color: '#f5a157', icon: '🧪', cmd: '/test ' },
          { name: 'Designer', desc: 'UI/UX from prompts', color: '#38bdf8', icon: '🎨', cmd: 'Design a UI for ' },
        ].map(a => (
          <div key={a.name} onClick={() => onOpenIDE(a.cmd)} style={{ background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', borderRadius: 10, padding: 16, cursor: 'pointer', transition: 'all 0.2s' }}
            onMouseEnter={e => { e.currentTarget.style.borderColor = `${a.color}40`; e.currentTarget.style.transform = 'translateY(-2px)' }}
            onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.06)'; e.currentTarget.style.transform = 'translateY(0)' }}
          >
            <div style={{ width: 32, height: 32, borderRadius: 8, background: `${a.color}18`, display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: 10 }}>
              <span style={{ fontSize: 14 }}>{a.icon}</span>
            </div>
            <div style={{ fontWeight: 700, fontSize: 12, color: '#fff' }}>{a.name}</div>
            <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.4)', marginTop: 4 }}>{a.desc}</div>
          </div>
        ))}
      </div>

      {/* Agent descriptions */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
        {[
          { name: 'Coder', color: '#ffd086', icon: '⚡', title: 'Code Generation & Editing', description: 'The Coder agent is your full-stack development partner. It reads your entire codebase, understands the architecture, and makes precise edits. Whether you need a new React component, an API endpoint, or a database migration — just describe what you want in plain English. Supports 20+ languages including TypeScript, Python, Go, and Rust.' },
          { name: 'Reviewer', color: '#f07070', icon: '🔍', title: 'Code Review & Bug Detection', description: 'The Reviewer agent performs deep static analysis on your code. It identifies security vulnerabilities (XSS, SQL injection, secrets exposure), performance bottlenecks, logic errors, and code quality issues. Get line-by-line feedback with suggested fixes, just like a senior engineer reviewing your PR.' },
          { name: 'Architect', color: '#7c6ef5', icon: '🏗', title: 'System Design & Architecture', description: 'The Architect agent helps you plan before you build. It designs database schemas, API contracts, folder structures, and system diagrams. Perfect for starting new projects, refactoring monoliths into microservices, or planning scalable infrastructure for growing teams.' },
          { name: 'Docs', color: '#5fd4a4', icon: '📖', title: 'Documentation Generation', description: 'The Docs agent analyzes your source code and generates comprehensive documentation automatically. It creates README files, API reference docs, JSDoc/TSDoc comments, usage examples, and architecture guides. Keep your docs always up-to-date without manual effort.' },
          { name: 'Tester', color: '#f5a157', icon: '🧪', title: 'Test Generation & Coverage', description: 'The Tester agent writes unit tests, integration tests, and end-to-end tests for your code. It supports Vitest, Jest, Playwright, and Cypress. Automatically detects edge cases, mocks dependencies, and aims for high coverage. Run tests directly from the IDE terminal.' },
          { name: 'Designer', color: '#38bdf8', icon: '🎨', title: 'UI/UX Design from Prompts', description: 'The Designer agent converts natural language descriptions into beautiful, responsive UI components. It generates Tailwind CSS layouts, animations, dark mode variants, and accessible interfaces. Describe a landing page, dashboard, or form — and get production-ready code instantly.' },
        ].map(a => (
          <div key={a.name} style={{ display: 'flex', gap: 16, padding: '20px 24px', borderRadius: 12, background: 'rgba(255,255,255,0.02)', border: '1px solid rgba(255,255,255,0.06)', transition: 'border-color 0.2s' }}
            onMouseEnter={e => (e.currentTarget.style.borderColor = `${a.color}30`)}
            onMouseLeave={e => (e.currentTarget.style.borderColor = 'rgba(255,255,255,0.06)')}
          >
            <div style={{ width: 44, height: 44, borderRadius: 10, background: `${a.color}12`, border: `1px solid ${a.color}25`, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
              <span style={{ fontSize: 20 }}>{a.icon}</span>
            </div>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#fff', marginBottom: 4 }}>{a.title}</div>
              <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.5)', lineHeight: 1.6 }}>{a.description}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  )

  const renderHome = () => (
    <>
      <div style={{ maxWidth: 700, margin: '0 auto 48px' }}>
        <h1 style={{ fontFamily: 'ui-sans-serif, system-ui, sans-serif', fontWeight: 900, fontSize: 28, color: '#fff', marginBottom: 8, textAlign: 'center' }}>What do you want to build?</h1>
        <p style={{ fontSize: 13, color: 'rgba(255,255,255,0.4)', textAlign: 'center', marginBottom: 24, fontFamily: 'ui-monospace, monospace' }}>Describe your app and PULSE will generate it.</p>
        <div style={{ position: 'relative', borderRadius: 14, padding: 2, background: 'conic-gradient(from var(--glow-angle, 0deg), #f79708, #ce2432, #7c6ef5, #5fd4a4, #f5a157, #f79708)', animation: 'border-glow-rotate 4s linear infinite' }}>
          <div style={{ background: 'rgba(0,0,0,0.9)', borderRadius: 12, padding: '16px 18px' }}>
            <textarea ref={inputRef} value={input} onChange={e => setInput(e.target.value)} onKeyDown={handleKeyDown} placeholder="Build me a CRM for a dentist office..." rows={3} style={{ width: '100%', background: 'none', border: 'none', color: '#fff', fontSize: 14, fontFamily: 'ui-monospace, monospace', resize: 'none', outline: 'none', lineHeight: 1.6 }} />
            <div style={{ display: 'flex', alignItems: 'center', marginTop: 10 }}>
              <div style={{ flex: 1 }} />
              <button onClick={handleSend} disabled={!input.trim() || chatLoading} style={{ padding: '8px 20px', borderRadius: 8, border: 'none', background: 'linear-gradient(135deg, #f79708, #ce2432)', color: '#fff', fontWeight: 700, fontSize: 12, cursor: 'pointer', opacity: input.trim() && !chatLoading ? 1 : 0.4 }}>Build</button>
            </div>
          </div>
        </div>
      </div>
      {chatMessages.length > 0 && (
        <div style={{ maxWidth: 700, margin: '0 auto 40px' }}>
          {chatMessages.map((m, i) => (
            <div key={i} style={{ margin: '8px 0', padding: '12px 16px', borderRadius: 10, background: m.role === 'user' ? 'rgba(255,208,134,0.06)' : 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', color: '#eee', fontSize: 13, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
              <span style={{ fontSize: 10, color: m.role === 'user' ? '#ffd086' : '#5fd4a4', fontWeight: 700, display: 'block', marginBottom: 4 }}>{m.role === 'user' ? 'You' : 'PULSE AI'}</span>
              {m.text}
            </div>
          ))}
          {chatLoading && <div style={{ padding: 12, fontSize: 13 }}><span style={{ display: 'inline-block', animation: 'pulse-heartbeat 1.4s ease-in-out infinite' }}>✨</span> <span style={{ color: 'rgba(255,255,255,0.5)' }}>Building your app...</span></div>}
          <div ref={chatEndRef} />
        </div>
      )}
      {projects.length > 0 && (
        <div style={{ marginBottom: 48 }}>
          <div style={{ display: 'flex', alignItems: 'center', marginBottom: 16 }}>
            <h2 style={{ fontSize: 16, fontWeight: 700, color: '#fff' }}>Recent Projects</h2>
            <div style={{ flex: 1 }} />
            <button onClick={() => setView('projects')} style={{ fontSize: 11, color: 'rgba(255,255,255,0.4)', background: 'none', border: 'none', cursor: 'pointer' }}>View all</button>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 14 }}>
            {projects.slice(0, 6).map(p => renderProjectCard(p))}
          </div>
        </div>
      )}
      <div>
        <h2 style={{ fontSize: 16, fontWeight: 700, color: '#fff', marginBottom: 16 }}>Start from a template</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 10 }}>
          {TEMPLATES.map(t => (
            <button key={t.name} onClick={() => { setInput(`Build me a ${t.name.toLowerCase()} app`); inputRef.current?.focus() }} style={{ padding: '16px 14px', borderRadius: 10, border: '1px solid rgba(255,255,255,0.06)', background: 'rgba(255,255,255,0.02)', cursor: 'pointer', textAlign: 'left', transition: 'border-color 0.15s' }}
              onMouseEnter={e => (e.currentTarget.style.borderColor = `${t.color}44`)}
              onMouseLeave={e => (e.currentTarget.style.borderColor = 'rgba(255,255,255,0.06)')}>
              <div style={{ fontSize: 12, fontWeight: 700, color: '#fff', marginBottom: 4 }}>{t.name}</div>
              <div style={{ fontSize: 10, color: 'rgba(255,255,255,0.35)' }}>{t.desc}</div>
            </button>
          ))}
        </div>
      </div>
    </>
  )

  return (
    <div className="app-bg" ref={containerRef} onPointerMove={onPointerMove}>
      <div className="app-bg-gradient"><div className="app-bg-gradient-top" /><div className="app-bg-gradient-bottom" /></div>
      <div className="cursor-spot" />
      <div style={{ position: 'relative', zIndex: 5, height: '100vh', display: 'flex' }}>
        {/* Sidebar */}
        <aside style={{ width: 220, borderRight: '1px solid rgba(255,255,255,0.06)', display: 'flex', flexDirection: 'column', padding: '16px 0', background: 'rgba(0,0,0,0.4)', backdropFilter: 'blur(12px)', overflow: 'auto' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '0 16px', marginBottom: 20 }}>
            <div style={{ width: 26, height: 26, borderRadius: 6, background: 'linear-gradient(135deg, #f79708, #ce2432)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 11, fontWeight: 900, color: '#fff' }}>P</div>
            <span style={{ fontWeight: 900, fontSize: 13, color: '#fff', letterSpacing: '0.1em' }}>PULSE</span>
          </div>
          <nav style={{ flex: 1 }}>
            {SIDEBAR.map((group, gi) => (
              <div key={gi}>
                {group.section && <div style={{ fontSize: 9, fontWeight: 700, color: 'rgba(255,255,255,0.25)', padding: '12px 16px 4px', letterSpacing: '0.08em', textTransform: 'uppercase' }}>{group.section}</div>}
                { group.items.filter(item => item.id !== "admin" || user?.role === "admin").map(item => (
                  <button key={item.id} onClick={() => setView(item.id)} style={{
                    display: 'flex', alignItems: 'center', gap: 9, width: '100%', padding: '7px 16px',
                    background: view === item.id ? 'rgba(255,208,134,0.08)' : 'none',
                    border: 'none', borderLeft: view === item.id ? '2px solid #ffd086' : '2px solid transparent',
                    color: view === item.id ? '#fff' : 'rgba(255,255,255,0.45)',
                    cursor: 'pointer', fontSize: 11, fontFamily: 'ui-monospace, monospace', textAlign: 'left', transition: 'all 0.12s',
                  }}>
                    <span style={{ width: 16, display: 'flex', alignItems: 'center', justifyContent: 'center', opacity: view === item.id ? 1 : 0.6 }}>
                      <span style={{ fontSize: 13 }}>{item.icon}</span>
                    </span>
                    <span>{item.label}</span>
                  </button>
                ))}
              </div>
            ))}
          </nav>
          <div style={{ padding: '10px 16px', borderTop: '1px solid rgba(255,255,255,0.06)' }}>
            <button onClick={() => onOpenIDE()} style={{ display: 'flex', alignItems: 'center', gap: 8, width: '100%', padding: '8px 12px', borderRadius: 8, border: '1px solid rgba(255,208,134,0.25)', background: 'rgba(255,208,134,0.06)', color: '#ffd086', cursor: 'pointer', fontSize: 11, fontFamily: 'ui-monospace, monospace', fontWeight: 700, marginBottom: 10 }}>
              <span style={{ fontSize: 13 }}>⚡</span>
              <span>Open IDE</span>
            </button>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
              <div style={{ width: 26, height: 26, borderRadius: '50%', background: 'linear-gradient(135deg, #7c6ef5, #5fd4a4)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 10, fontWeight: 700, color: '#fff' }}>
                {(user?.name || user?.email || 'U')[0].toUpperCase()}
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 10, fontWeight: 600, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{user?.name || user?.email}</div>
                <div style={{ fontSize: 8, color: 'rgba(255,255,255,0.35)' }}>{(user?.plan || 'free').toUpperCase()}</div>
              </div>
              <button onClick={() => logout()} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.25)', cursor: 'pointer', fontSize: 12 }} title="Sign out">x</button>
            </div>
          </div>
        </aside>
        {/* Main */}
        <main style={{ flex: 1, overflow: 'auto', padding: view === 'home' ? '40px 48px' : 0 }}>
          {renderContent()}
        </main>
      </div>
    </div>
  )
}

const cardBtnStyle: React.CSSProperties = { padding: '4px 10px', borderRadius: 6, border: '1px solid rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.03)', color: 'rgba(255,255,255,0.6)', fontSize: 10, cursor: 'pointer', fontFamily: 'ui-monospace, monospace' }

function ChatHistoryView({ chatMessages, setChatMessages }: { chatMessages: { role: string; text: string }[]; setChatMessages: (m: { role: string; text: string }[]) => void }) {
  const [conversations, setConversations] = useState<any[]>([])
  const [loading, setLoading] = useState(true)
  const [activeId, setActiveId] = useState<string | null>(null)
  const [messages, setMessages] = useState<{ role: string; text: string }[]>([])
  const [hasCodeChanges, setHasCodeChanges] = useState(false)
  const [reverting, setReverting] = useState(false)

  useEffect(() => {
    authFetch(apiUrl('/api/history')).then(r => r.ok ? r.json() : []).then(d => setConversations(Array.isArray(d) ? d : [])).catch(() => {}).finally(() => setLoading(false))
  }, [])

  const openConv = async (id: string) => {
    setActiveId(id)
    setHasCodeChanges(false)
    try {
      const res = await authFetch(apiUrl(`/api/history/${id}`))
      const data = await res.json()
      const msgs = (data.messages ?? []).map((m: any) => ({ role: m.role, text: m.content }))
      setMessages(msgs)
      // Detect if AI generated/modified code in this conversation
      const codeIndicators = ['FILE:', '```', 'Created file', 'Updated file', 'created with', 'files!', '\u2705 Project']
      const hadChanges = msgs.some((m: any) => m.role === 'assistant' && codeIndicators.some(ind => m.text?.includes(ind)))
      setHasCodeChanges(hadChanges)
    } catch {}
  }

  const deleteConv = async (id: string, e: React.MouseEvent) => {
    e.stopPropagation()
    if (!window.confirm('Delete this conversation?')) return
    try { await authFetch(apiUrl(`/api/history/${id}`), { method: 'DELETE' }) } catch {}
    setConversations(prev => prev.filter(c => c.id !== id))
    if (activeId === id) { setActiveId(null); setMessages([]) }
  }

  const revertToConv = async () => {
    if (!window.confirm('Revert your workspace to this point? Files created after this conversation will be removed.')) return
    setReverting(true)
    // Find the conversation to get its timestamp
    const conv = conversations.find(c => c.id === activeId)
    if (conv) {
      try {
        await authFetch(apiUrl('/api/files/revert'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ beforeDate: conv.updated_at }),
        })
        window.dispatchEvent(new CustomEvent('pulse:files-changed'))
      } catch {}
    }
    setReverting(false)
  }

  const formatTime = (dateStr: string) => {
    const d = new Date(dateStr)
    return d.toLocaleString('en', { month: 'short', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })
  }

  const formatDate = (dateStr: string) => {
    const d = new Date(dateStr)
    const now = new Date()
    const diff = now.getTime() - d.getTime()
    if (diff < 86400000) return 'Today ' + d.toLocaleTimeString('en', { hour: '2-digit', minute: '2-digit' })
    if (diff < 172800000) return 'Yesterday ' + d.toLocaleTimeString('en', { hour: '2-digit', minute: '2-digit' })
    return d.toLocaleString('en', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
  }

  // Viewing a conversation
  if (activeId) {
    const conv = conversations.find(c => c.id === activeId)
    return (
      <div style={{ padding: '24px 32px', height: '100%', display: 'flex', flexDirection: 'column' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16, flexShrink: 0 }}>
          <button onClick={() => { setActiveId(null); setMessages([]) }} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.5)', cursor: 'pointer', fontSize: 12, fontFamily: 'ui-monospace, monospace' }}>← Back</button>
          <div style={{ flex: 1 }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: '#fff' }}>
              {conv?.title || 'Conversation'}
            </span>
            {conv?.updated_at && (
              <span style={{ fontSize: 9, color: 'rgba(255,255,255,0.3)', fontFamily: 'ui-monospace, monospace', marginLeft: 10 }}>
                {formatTime(conv.updated_at)}
              </span>
            )}
          </div>
          {hasCodeChanges && (
            <button onClick={revertToConv} disabled={reverting} style={{
              padding: '5px 12px', borderRadius: 6, border: '1px solid rgba(240,112,112,0.4)',
              background: 'rgba(240,112,112,0.08)', color: '#f07070', fontSize: 10,
              cursor: 'pointer', fontFamily: 'ui-monospace, monospace', fontWeight: 700,
              opacity: reverting ? 0.5 : 1,
            }}>
              {reverting ? 'Reverting...' : '↩ Revert to this point'}
            </button>
          )}
        </div>
        <div style={{ flex: 1, overflow: 'auto' }}>
          {messages.map((m, i) => (
            <div key={i} style={{ margin: '8px 0', padding: '12px 16px', borderRadius: 10, background: m.role === 'user' ? 'rgba(255,208,134,0.06)' : 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.06)', color: '#eee', fontSize: 13, lineHeight: 1.6, whiteSpace: 'pre-wrap' }}>
              <span style={{ fontSize: 10, color: m.role === 'user' ? '#ffd086' : '#5fd4a4', fontWeight: 700, display: 'block', marginBottom: 4 }}>{m.role === 'user' ? 'You' : 'PULSE AI'}</span>
              {m.text}
            </div>
          ))}
          {messages.length === 0 && <div style={{ color: 'rgba(255,255,255,0.3)', fontSize: 12 }}>No messages</div>}
        </div>
      </div>
    )
  }

  // Conversation list
  const [visibleCount, setVisibleCount] = useState(10)

  return (
    <div style={{ padding: '24px 32px' }}>
      <h2 style={{ fontSize: 18, fontWeight: 700, color: '#fff', marginBottom: 20 }}>Chat History</h2>
      {loading && <div style={{ color: 'rgba(255,255,255,0.4)', fontSize: 12 }}>Loading...</div>}
      {!loading && conversations.length === 0 && (
        <div style={{ color: 'rgba(255,255,255,0.3)', fontSize: 12 }}>No conversations yet.</div>
      )}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
        {conversations.slice(0, visibleCount).map(conv => (
          <div key={conv.id} style={{
            display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px',
            borderRadius: 10, border: '1px solid rgba(255,255,255,0.06)', background: 'rgba(255,255,255,0.02)',
            cursor: 'pointer', transition: 'border-color 0.15s',
          }}
            onClick={() => openConv(conv.id)}
            onMouseEnter={e => (e.currentTarget.style.borderColor = 'rgba(124,110,245,0.3)')}
            onMouseLeave={e => (e.currentTarget.style.borderColor = 'rgba(255,255,255,0.06)')}
          >
            <span style={{ fontSize: 16 }}>💬</span>
            <div style={{ flex: 1, minWidth: 0 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: '#fff', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                {conv.title || 'Untitled'}
              </div>
              <div style={{ fontSize: 9, color: 'rgba(255,255,255,0.3)', fontFamily: 'ui-monospace, monospace', marginTop: 2 }}>
                {formatDate(conv.updated_at || conv.created_at)}
              </div>
            </div>
            <button onClick={(e) => deleteConv(conv.id, e)} style={{ background: 'none', border: 'none', color: 'rgba(255,255,255,0.2)', cursor: 'pointer', fontSize: 14, padding: '2px 4px', borderRadius: 4 }}
              onMouseEnter={e => (e.currentTarget.style.color = '#f07070')}
              onMouseLeave={e => (e.currentTarget.style.color = 'rgba(255,255,255,0.2)')}
            >×</button>
          </div>
        ))}
      </div>
      {conversations.length > visibleCount && (
        <button onClick={() => setVisibleCount(c => c + 10)} style={{
          display: 'block', margin: '16px auto 0', padding: '8px 20px', borderRadius: 8,
          border: '1px solid rgba(124,110,245,0.3)', background: 'rgba(124,110,245,0.06)',
          color: '#7c6ef5', fontSize: 11, cursor: 'pointer', fontFamily: 'ui-monospace, monospace', fontWeight: 700,
        }}>
          Load more ({conversations.length - visibleCount} remaining)
        </button>
      )}
    </div>
  )
}
const chatInputStyle: React.CSSProperties = { flex: 1, padding: '10px 14px', borderRadius: 10, border: '1px solid rgba(255,255,255,0.08)', background: 'rgba(255,255,255,0.03)', color: '#fff', fontSize: 13, fontFamily: 'ui-monospace, monospace', resize: 'none', outline: 'none' }
const sendBtnStyle: React.CSSProperties = { padding: '10px 18px', borderRadius: 10, border: 'none', background: 'linear-gradient(135deg, #f79708, #ce2432)', color: '#fff', fontWeight: 700, fontSize: 12, cursor: 'pointer' }
const tabStyle: React.CSSProperties = { padding: '6px 14px', borderRadius: 6, border: '1px solid rgba(255,255,255,0.08)', background: 'none', color: 'rgba(255,255,255,0.5)', fontSize: 11, cursor: 'pointer', fontFamily: 'ui-monospace, monospace' }
const tabActiveStyle: React.CSSProperties = { ...tabStyle, background: 'rgba(255,208,134,0.1)', color: '#ffd086', borderColor: 'rgba(255,208,134,0.3)' }
const backBtnStyle: React.CSSProperties = { background: 'none', border: 'none', color: 'rgba(255,255,255,0.5)', cursor: 'pointer', fontSize: 12, fontFamily: 'ui-monospace, monospace' }
